/**
 * <kk-cart-drawer> — behaviour for sections/kk-cart-drawer.liquid
 *
 * - Opens on `kk:cart:open` (header cart icon, KK add-to-cart buttons).
 * - Native modal <dialog>: focus trap + Esc; backdrop click and [data-kk-cart-close] close it.
 * - Re-renders itself when the cart changes from outside KK code (e.g. Horizon's product form),
 *   and opens after those adds. KK changes are re-rendered by kk-cart.js directly.
 * - Opens on `kk:cart:error` so the alert line (filled by kk-cart.js) is visible.
 * - Theme editor: opens when the section is selected, closes when deselected.
 */
// Shopify's standard events file is an optional extra (it keeps Horizon's own cart parts in sync).
// It is downloaded from cdn.shopify.com; if it is blocked, slow or down, everything here still works.
let standardEvents = null;
try {
  standardEvents = await Promise.race([
    import('@shopify/events'),
    new Promise((_, reject) => setTimeout(() => reject(new Error('timed out')), 3000)),
  ]);
} catch (error) {
  console.warn('[KK] Shopify standard events unavailable; cart works without syncing Horizon parts.', error);
}
const CART_LINES_UPDATE = standardEvents?.StandardEvents?.cartLinesUpdate ?? 'shopify:cart:lines-update';


const ROOT = window.Shopify?.routes?.root || '/';

class KKCartDrawer extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
    if (!this.dialog) return;

    document.addEventListener('kk:cart:open', () => this.open(), { signal });
    document.addEventListener('kk:cart:error', (event) => this.#showError(event.detail?.message), { signal });
    document.addEventListener('kk:cart:updated', () => this.#afterRender(), { signal });
    document.addEventListener(CART_LINES_UPDATE, (event) => this.#onExternalUpdate(event), { signal });

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (target === this.dialog || target.closest('[data-kk-cart-close]')) this.close();
      },
      { signal }
    );

    // Any cart icon on the page opens this drawer — including Horizon's own header cart button
    // (aria-controls="cart-drawer") and plain links to the cart page inside a header/navigation.
    // Capture phase, so it runs before Horizon's handlers and before the browser follows the link.
    document.addEventListener(
      'click',
      (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
        const target = event.target instanceof Element ? event.target : null;
        const trigger = target?.closest(
          '[data-kk-cart-open], [aria-controls="cart-drawer"], [data-testid="cart-drawer-trigger"], header a[href$="/cart"], [role="banner"] a[href$="/cart"], [id^="header"] a[href$="/cart"]'
        );
        if (!trigger || trigger.closest('kk-cart-drawer')) return;
        event.preventDefault();
        event.stopPropagation();
        this.open();
      },
      { capture: true, signal }
    );
    const requestedAt = Number(document.documentElement.dataset.kkCartOpenRequested || 0);
    if (requestedAt && Date.now() - requestedAt < 8000) this.open();

    this.dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      document.querySelectorAll('[data-kk-cart-open], [aria-controls="cart-drawer"]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    }, { signal });

    window.addEventListener('pageshow', (event) => event.persisted && this.refresh(), { signal });

    // Shopify's standard "open cart" action (used by apps/Shopify buttons) opens this drawer.
    // Runs after Horizon's standard-actions-override.js, which configures it on DOMContentLoaded.
    const hookOpenCartAction = () =>
      setTimeout(() => window.Shopify?.actions?.openCart?.configure?.({ handler: async () => this.open() }));
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', hookOpenCartAction, { once: true, signal });
    } else {
      hookOpenCartAction();
    }

    document.addEventListener(
      'shopify:section:select',
      (event) => event.detail?.sectionId === this.dataset.sectionId && this.open(),
      { signal }
    );
    document.addEventListener(
      'shopify:section:deselect',
      (event) => event.detail?.sectionId === this.dataset.sectionId && this.close(),
      { signal }
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
    document.documentElement.style.overflow = '';
  }

  open() {
    if (!this.dialog) return;
    delete document.documentElement.dataset.kkCartOpenRequested;
    if (!this.dialog.open) {
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
    // Keep every cart trigger's aria-expanded honest.
    document.querySelectorAll('[data-kk-cart-open], [aria-controls="cart-drawer"]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
  }

  toggle() {
    if (this.dialog?.open) this.close();
    else this.open();
  }

  close() {
    this.dialog?.close();
  }

  /** Fetch this section again and swap the cart region. */
  async refresh() {
    const id = this.dataset.sectionId;
    if (!id) return;
    try {
      const response = await fetch(`${ROOT}?sections=${encodeURIComponent(id)}`);
      const sections = await response.json();
      const html = sections?.[id];
      if (!html) return;

      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(`[data-kk-cart-section="${id}"]`);
      const current = this.querySelector(`[data-kk-cart-section="${id}"]`);
      if (fresh && current) current.replaceWith(document.importNode(fresh, true));
      this.#afterRender();
    } catch {
      // Keep the current content; the next change will try again.
    }
  }

  /** Cart changed by something other than kk-cart.js (e.g. Horizon's product form). */
  #onExternalUpdate(event) {
    event.promise
      ?.then((result) => {
        if (result?.detail?.source === 'kk-cart') return;
        return this.refresh().then(() => {
          if (event.action === 'add' && !result?.detail?.didError) this.open();
        });
      })
      .catch(() => {});
  }

  /** After a re-render: keep focus inside the open drawer (e.g. when the focused line was removed). */
  #afterRender() {
    if (!this.dialog?.open) return;
    if (!this.dialog.contains(document.activeElement)) {
      /** @type {HTMLElement | null} */ (this.dialog.querySelector('[data-kk-cart-close]'))?.focus();
    }
  }

  /** kk-cart.js fills the [data-kk-cart-error] line; open so the customer sees it. */
  #showError(message) {
    if (message) this.open();
  }
}

if (!customElements.get('kk-cart-drawer')) {
  customElements.define('kk-cart-drawer', KKCartDrawer);
}
